import { supabase } from './supabase';
import { TABLES } from './tables';

export const MONTH_LABELS = ['sep', 'oct', 'nov', 'dec', 'ian', 'feb', 'mar', 'apr', 'mai', 'iun'];

export const CATEGORY_LABELS = {
  curricular: 'Curriculare',
  extracurricular: 'Extracurriculare',
  transferabil: 'Transferabile',
};

export const CATEGORY_COLORS = {
  curricular: '#1976D2',
  extracurricular: '#F57C00',
  transferabil: '#7B1FA2',
};

export const getPreviousSchoolYear = (year) => {
  const match = /^(\d{4})-(\d{4})$/.exec(year || '');
  if (!match) return null;
  return `${Number(match[1]) - 1}-${Number(match[2]) - 1}`;
};

export const schoolMonthIndex = (dateIso) => {
  const date = new Date(dateIso);
  const month = date.getMonth() + 1;
  if (month >= 9) return month - 9;
  return month + 3;
};

export const downloadCsv = (filename, headers, rows) => {
  const escape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [headers.map(escape).join(',')];
  rows.forEach((row) => lines.push(row.map(escape).join(',')));
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadExcel = (filename, headers, rows) => {
  const escapeHtml = (value) =>
    String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const headerHtml = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
  const bodyHtml = rows
    .map((row) => {
      if (!row || row.length === 0) return '<tr></tr>';
      return `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`;
    })
    .join('');
  const html = `\uFEFF<html><head><meta charset="utf-8" /></head><body><table border="1"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const loadStatisticsData = async (year) => {
  let requestsQuery = supabase
    .from(TABLES.credit_requests)
    .select(
      'id, id_elev, id_tip_credit, id_module, nume_activitate, ore_realizate, credite_solicitate, credite_acordate, status, data_cerere, data_validare, an_scolar, source_system, external_user_id, idempotency_key, source_id',
    )
    .order('data_cerere', { ascending: true });

  if (year && year !== 'all') requestsQuery = requestsQuery.eq('an_scolar', year);

  const [studentsRes, requestsRes, typesRes, modulesRes] = await Promise.all([
    supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
    requestsQuery,
    supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
    supabase.from(TABLES.modules).select('id, cod_module, nume_module, credite_tinta, ordine').order('ordine'),
  ]);

  return {
    students: studentsRes.data || [],
    requests: requestsRes.data || [],
    creditTypes: typesRes.data || [],
    modules: modulesRes.data || [],
  };
};

const median = (values) => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

export const linearRegression = (values) => {
  const points = values.map((value, index) => ({ x: index, y: value }));
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: values[0] || 0 };
  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);
  const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
  const sumXX = points.reduce((sum, p) => sum + p.x * p.x, 0);
  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

export const computeStatistics = (data, filters = {}) => {
  const { classFilter = 'all', specFilter = 'all', categoryFilter = 'all' } = filters;
  const { students, requests, creditTypes, modules } = data;

  const typeById = new Map((creditTypes || []).map((t) => [t.id, t]));
  const moduleById = new Map((modules || []).map((m) => [m.id, m]));

  const filteredStudents = (students || []).filter(
    (s) =>
      (classFilter === 'all' || s.clasa === classFilter) &&
      (specFilter === 'all' || s.specializare === specFilter),
  );
  const studentById = new Map(filteredStudents.map((s) => [s.id, s]));
  const studentIds = new Set(studentById.keys());

  const studentRequests = (requests || []).filter((r) => studentIds.has(r.id_elev));
  const validated = studentRequests.filter((r) => r.status === 'validat');
  const creditsOf = (r) => r.credite_acordate || r.credite_solicitate || 0;

  const filteredValidated = validated.filter((r) => {
    if (categoryFilter === 'all') return true;
    const categorie = typeById.get(r.id_tip_credit)?.categorie || 'curricular';
    return categorie === categoryFilter;
  });

  const byModule = (modules || []).map((m) => {
    const rows = filteredValidated.filter((r) => r.id_module === m.id);
    return {
      cod: m.cod_module,
      nume: m.nume_module,
      credite: rows.reduce((sum, r) => sum + creditsOf(r), 0),
      cereri: rows.length,
    };
  });

  const categoryTotals = { curricular: 0, extracurricular: 0, transferabil: 0 };
  filteredValidated.forEach((r) => {
    const categorie = typeById.get(r.id_tip_credit)?.categorie || 'curricular';
    categoryTotals[categorie] = (categoryTotals[categorie] || 0) + creditsOf(r);
  });

  const totalsByStudent = new Map();
  filteredValidated.forEach((r) => {
    const current = totalsByStudent.get(r.id_elev) || {
      curricular: 0,
      extracurricular: 0,
      transferabil: 0,
      total: 0,
      modules: {},
    };
    const categorie = typeById.get(r.id_tip_credit)?.categorie || 'curricular';
    const credits = creditsOf(r);
    current[categorie] += credits;
    current.total += credits;
    const cod = moduleById.get(r.id_module)?.cod_module;
    if (cod) current.modules[cod] = (current.modules[cod] || 0) + credits;
    totalsByStudent.set(r.id_elev, current);
  });

  const targetSum = (modules || []).reduce((sum, m) => sum + (m.credite_tinta || 0), 0);

  const classGroups = new Map();
  filteredStudents.forEach((s) => {
    const clasa = s.clasa || 'Fara clasa';
    const list = classGroups.get(clasa) || [];
    list.push(s.id);
    classGroups.set(clasa, list);
  });

  const byClass = [...classGroups.entries()]
    .map(([clasa, ids]) => {
      const totals = ids.map((id) => totalsByStudent.get(id)?.total || 0);
      const sum = totals.reduce((acc, value) => acc + value, 0);
      return {
        clasa,
        elevi: ids.length,
        total: sum,
        medie: ids.length ? sum / ids.length : 0,
        median: median(totals),
        min: totals.length ? Math.min(...totals) : 0,
        max: totals.length ? Math.max(...totals) : 0,
        zero: totals.filter((value) => value === 0).length,
        peste80: totals.filter((value) => targetSum > 0 && value >= targetSum * 0.8).length,
      };
    })
    .sort((a, b) => a.clasa.localeCompare(b.clasa));

  const studentSummaries = filteredStudents.map((s) => {
    const totals = totalsByStudent.get(s.id) || {
      curricular: 0,
      extracurricular: 0,
      transferabil: 0,
      total: 0,
      modules: {},
    };
    const classmates = (classGroups.get(s.clasa || 'Fara clasa') || [])
      .map((id) => totalsByStudent.get(id)?.total || 0)
      .sort((a, b) => b - a);
    const rank = classmates.indexOf(totals.total) + 1;
    const classMean =
      classmates.length > 0 ? classmates.reduce((acc, value) => acc + value, 0) / classmates.length : 0;
    const status = totals.total === 0 ? 'risc' : totals.total < classMean ? 'sub' : 'ok';
    return {
      id: s.id,
      nume: s.nume,
      prenume: s.prenume,
      clasa: s.clasa,
      specializare: s.specializare,
      curricular: totals.curricular,
      extracurricular: totals.extracurricular,
      transferabil: totals.transferabil,
      total: totals.total,
      modules: totals.modules,
      rank,
      classSize: classmates.length,
      status,
    };
  });

  const bySpecialization = [...new Set(filteredStudents.map((s) => s.specializare).filter(Boolean))]
    .map((spec) => {
      const ids = filteredStudents.filter((s) => s.specializare === spec).map((s) => s.id);
      const credits = ids.reduce((sum, id) => sum + (totalsByStudent.get(id)?.total || 0), 0);
      return { specializare: spec, elevi: ids.length, credite: credits };
    })
    .sort((a, b) => b.credite - a.credite);

  const monthCredits = new Array(10).fill(0);
  const monthRequests = new Array(10).fill(0);
  const monthCumulative = new Array(10).fill(0);
  filteredValidated.forEach((r) => {
    const index = schoolMonthIndex(r.data_validare || r.data_cerere);
    if (index >= 0 && index < 10) monthCredits[index] += creditsOf(r);
  });
  studentRequests.forEach((r) => {
    const index = schoolMonthIndex(r.data_cerere);
    if (index >= 0 && index < 10) monthRequests[index] += 1;
  });
  let running = 0;
  monthCredits.forEach((value, index) => {
    running += value;
    monthCumulative[index] = running;
  });

  const now = new Date();
  const currentMonthIndex = Math.min(9, Math.max(0, schoolMonthIndex(now.toISOString())));
  const { slope } = linearRegression(monthCumulative.slice(0, currentMonthIndex + 1));
  const predictedFinal = Math.max(
    monthCumulative[currentMonthIndex],
    monthCumulative[currentMonthIndex] + slope * (9 - currentMonthIndex),
  );

  const months = MONTH_LABELS.map((luna, index) => ({
    luna,
    credite: monthCredits[index],
    cereri: monthRequests[index],
    cumulat: monthCumulative[index],
  }));

  const activityMap = new Map();
  filteredValidated.forEach((r) => {
    const key = r.nume_activitate || 'Fara nume';
    const current = activityMap.get(key) || { nume: key, cereri: 0, credite: 0 };
    current.cereri += 1;
    current.credite += creditsOf(r);
    activityMap.set(key, current);
  });
  const topActivities = [...activityMap.values()].sort((a, b) => b.cereri - a.cereri).slice(0, 10);

  const topStudents = [...studentSummaries].sort((a, b) => b.total - a.total).slice(0, 10);

  const allCredits = studentSummaries.map((s) => s.total);
  const totalCredits = allCredits.reduce((sum, value) => sum + value, 0);
  const expectedProgress = targetSum * ((currentMonthIndex + 1) / 10);

  const sourceCounts = { manual: 0, srbhub: 0 };
  studentRequests.forEach((r) => {
    if (r.source_system === 'srbhub') sourceCounts.srbhub += 1;
    else sourceCounts.manual += 1;
  });

  const zeroCredits = studentSummaries.filter((s) => s.total === 0);
  const belowHalfMean = studentSummaries.filter((s) => {
    const ids = classGroups.get(s.clasa || 'Fara clasa') || [];
    const classMean = ids.length
      ? ids.reduce((sum, id) => sum + (totalsByStudent.get(id)?.total || 0), 0) / ids.length
      : 0;
    return s.total > 0 && classMean > 0 && s.total < classMean * 0.5;
  });

  return {
    filters,
    students: studentSummaries,
    byModule,
    categoryTotals,
    bySpecialization,
    byClass,
    months,
    topActivities,
    topStudents,
    sourceCounts,
    zeroCredits,
    belowHalfMean,
    totalCredits,
    average: allCredits.length ? totalCredits / allCredits.length : 0,
    median: median(allCredits),
    min: allCredits.length ? Math.min(...allCredits) : 0,
    max: allCredits.length ? Math.max(...allCredits) : 0,
    totalRequests: studentRequests.length,
    validatedCount: validated.length,
    pendingCount: studentRequests.filter((r) => r.status === 'in_asteptare').length,
    rejectedCount: studentRequests.filter((r) => r.status === 'respins').length,
    approvalRate: studentRequests.length ? (validated.length / studentRequests.length) * 100 : 0,
    targetSum,
    currentMonthIndex,
    slope,
    predictedFinal,
    expectedProgress,
    onTrack: studentSummaries.filter((s) => s.total >= expectedProgress).length,
  };
};

export const computeComparison = (current, previous) => {
  if (!previous) return { currentToDate: 0, previousToDate: 0, percent: null };
  const index = current.currentMonthIndex;
  const currentToDate = current.months[index]?.cumulat || 0;
  const previousToDate = previous.months[index]?.cumulat || 0;
  return {
    currentToDate,
    previousToDate,
    percent: previousToDate > 0 ? (currentToDate / previousToDate) * 100 : null,
  };
};
